import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logger } from '@core/utils';
import config from '@core/config';

/**
 * Custom Base Query with Enhanced Error Handling
 *
 * Provides:
 * - Automatic authentication token injection
 * - Normalized error responses
 * - Network error handling
 * - HTTP status code transformation
 *
 * Follows 2025 RTK Query best practices:
 * - Single source of truth for API configuration
 * - Consistent error format across all endpoints
 * - Automatic retry logic for specific errors
 */

/**
 * Base query with automatic token injection
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    // Try Redux state first, fallback to localStorage for consistency
    const token = getState().auth?.token || localStorage.getItem('token');

    if (token) {
      headers.set('Authorization', `Token ${token}`);
    }

    // Set default content type if not already set
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  },
});

/**
 * Transform error responses to consistent format
 * @param {Object} error - RTK Query error object
 * @returns {Object} Normalized error object
 */
function transformError(error) {
  // Network error (no response from server)
  if (error.error) {
    return {
      status: 'FETCH_ERROR',
      error: 'Network error. Please check your connection.',
      originalError: error.error,
    };
  }

  // HTTP error with response
  if (error.status) {
    const status = error.status;
    const data = error.data;

    // Extract error message from various backend response formats
    let message = 'An error occurred';

    if (typeof data === 'string') {
      message = data;
    } else if (data?.detail) {
      message = data.detail;
    } else if (data?.message) {
      message = data.message;
    } else if (data?.error) {
      message = data.error;
    } else if (data?.non_field_errors) {
      message = Array.isArray(data.non_field_errors)
        ? data.non_field_errors.join(', ')
        : data.non_field_errors;
    }

    // Handle specific HTTP status codes
    switch (status) {
      case 401:
        message = 'Authentication required. Please log in.';
        break;
      case 403:
        message = 'You do not have permission to perform this action.';
        break;
      case 404:
        message = 'The requested resource was not found.';
        break;
      case 429:
        message = 'Too many requests. Please try again later.';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        message = 'Server error. Please try again later.';
        break;
    }

    return {
      status,
      error: message,
      data,
      originalError: error,
    };
  }

  // Unknown error format
  return {
    status: 'UNKNOWN_ERROR',
    error: 'An unexpected error occurred',
    originalError: error,
  };
}

/**
 * Enhanced base query with error transformation
 * @param {string|Object} args - Request arguments
 * @param {Object} api - RTK Query API object
 * @param {Object} extraOptions - Extra options
 * @returns {Promise} Query result
 */
export const baseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  // Log errors in development
  if (result.error) {
    if (process.env.NODE_ENV === 'development') {
      logger.error('API Error:', {
        endpoint: typeof args === 'string' ? args : args.url,
        error: result.error,
      });
    }
  }

  // Transform error to consistent format
  if (result.error) {
    result.error = transformError(result.error);
  }

  return result;
};

/**
 * Base query for public endpoints (no authentication)
 */
export const publicBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,
  prepareHeaders: (headers) => {
    // PUBLIC endpoint - no authentication required
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
});
